import React, { useState, useEffect } from 'react';
import {
  Box,
  Paper,
  Typography,
  Grid,
  Card,
  CardContent,
  CardHeader,
  CardMedia,
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Chip,
  IconButton,
  Alert,
  CircularProgress,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Tabs,
  Tab,
  List,
  ListItem,
  ListItemText,
  ListItemIcon,
  ListItemSecondaryAction,
  Tooltip,
  Badge,
  Avatar,
  Divider,
  Stepper,
  Step,
  StepLabel,
  StepContent,
  FormControlLabel,
  Switch,
  InputAdornment,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  LinearProgress,
  AlertTitle,
  Snackbar,
  Drawer,
  AppBar,
  Toolbar,
  Breadcrumbs,
  Link,
  Rating,
  ListItemButton,
  ListItemAvatar,
  CardActions,
} from '@mui/material';
import { useRbac } from '../../context/RbacContext';
import { format, parseISO } from 'date-fns';
import {
  Add,
  Edit,
  Delete,
  Visibility,
  Search,
  FilterList,
  Sort,
  Refresh,
  TrendingUp,
  TrendingDown,
  ExpandMore,
  Close,
  ArrowBack,
  ArrowForward,
  FirstPage,
  LastPage,
  KeyboardArrowLeft,
  KeyboardArrowRight,
  Notifications,
  Settings,
  Help,
  ContactSupport,
  History,
  Assessment,
  Analytics,
  Timeline,
  Receipt,
  Payment,
  AccountBalance,
  QrCode,
  Description,
  CheckCircle,
  Error,
  Warning,
  Info,
  CalendarToday,
  AccessTime,
  Person,
  Business,
  LocationOn,
  Phone,
  Email,
  AttachMoney,
  Inventory,
  ProductionQuantityLimits,
  Assignment,
  Work,
  Category,
  Palette,
  Straighten,
  Scale,
  Security,
  Verified,
  Lock,
  Print,
  Download,
  Share,
  Favorite,
  FavoriteBorder,
  Star,
  StarBorder,
  ShoppingCart,
  AddShoppingCart,
  RemoveShoppingCart,
  ShoppingBasket,
  Assessment as AssessmentIcon,
  Analytics as AnalyticsIcon,
  Receipt as ReceiptIcon,
  Payment as PaymentIcon,
  AccountBalance as AccountBalanceIcon,
  QrCode as QrCodeIcon,
  Description as DescriptionIcon,
  CheckCircle as CheckCircleIcon,
  Error as ErrorIcon,
  Warning as WarningIcon,
  Info as InfoIcon,
  LocalShipping as LocalShippingIcon,
  Build as BuildIcon,
  Engineering as EngineeringIcon,
  Speed as SpeedIcon,
  Timer as TimerIcon,
  CalendarToday as CalendarTodayIcon,
  AccessTime as AccessTimeIcon,
  Person as PersonIcon,
  Business as BusinessIcon,
  LocationOn as LocationOnIcon,
  Phone as PhoneIcon,
  Email as EmailIcon,
  AttachMoney as AttachMoneyIcon,
  Inventory as InventoryIcon,
  ProductionQuantityLimits as ProductionQuantityLimitsIcon,
  Assignment as AssignmentIcon,
  Work as WorkIcon,
  Category as CategoryIcon,
  Palette as PaletteIcon,
  Straighten as StraightenIcon,
  Scale as ScaleIcon,
  Security as SecurityIcon,
  Verified as VerifiedIcon,
  Lock as LockIcon,
  Print as PrintIcon,
  Download as DownloadIcon,
  Share as ShareIcon,
  Favorite as FavoriteIcon,
  FavoriteBorder as FavoriteBorderIcon,
  Star as StarIcon,
  StarBorder as StarBorderIcon,
  ShoppingCart as ShoppingCartIcon,
  AddShoppingCart as AddShoppingCartIcon,
  RemoveShoppingCart as RemoveShoppingCartIcon,
  ShoppingBasket as ShoppingBasketIcon,
} from '@mui/icons-material';
import { uploadFile } from '../../api/uploadApi';

interface Product {
  id: string;
  name: string;
  description: string;
  price: number;
  category: string;
  subcategory: string;
  brand: string;
  sku: string;
  barcode: string;
  images: string[];
  status: 'active' | 'inactive' | 'discontinued' | 'out_of_stock';
  inStock: boolean;
  stockQuantity: number;
  minStockLevel: number;
  maxStockLevel: number;
  weight: number;
  dimensions: {
    length: number;
    width: number;
    height: number;
  };
  materials: string[];
  colors: string[];
  sizes: string[];
  tags: string[];
  rating: number;
  reviewCount: number;
  createdAt: string;
  updatedAt: string;
  createdBy: string;
  updatedBy: string;
  cost: number;
  profitMargin: number;
  supplier: string;
  warranty: string;
  specifications: Record<string, any>;
  variants: ProductVariant[];
}

interface ProductVariant {
  id: string;
  productId: string;
  sku: string;
  color: string;
  size: string;
  material: string;
  price: number;
  stockQuantity: number;
  images: string[];
  status: 'active' | 'inactive';
}

interface ProductAnalytics {
  totalProducts: number;
  activeProducts: number;
  outOfStockProducts: number;
  lowStockProducts: number;
  totalValue: number;
  averagePrice: number;
  topCategories: Array<{
    name: string;
    count: number;
    percentage: number;
  }>;
  topProducts: Array<{
    name: string;
    sales: number;
    revenue: number;
  }>;
}

export const ProductManagement: React.FC = () => {
  const { canManage } = useRbac();
  const [selectedTab, setSelectedTab] = useState(0);
  const [openDialog, setOpenDialog] = useState(false);
  const [viewingProduct, setViewingProduct] = useState<Product | null>(null);
  const [selectedProduct, setSelectedProduct] = useState<Product | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [loading, setLoading] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const [filter, setFilter] = useState({
    status: 'all',
    category: 'all',
    inStock: 'all'
  });
  const [sortBy, setSortBy] = useState('name');
  const [sortOrder, setSortOrder] = useState<'asc' | 'desc'>('asc');
  const [newProduct, setNewProduct] = useState<Partial<Product>>({
    name: '',
    description: '',
    price: 0,
    category: '',
    subcategory: '',
    brand: '',
    sku: '',
    status: 'active',
    inStock: true,
    stockQuantity: 0,
    minStockLevel: 10,
    maxStockLevel: 100,
    weight: 0,
    dimensions: { length: 0, width: 0, height: 0 },
    materials: [],
    colors: [],
    sizes: [],
    tags: [],
    cost: 0,
    supplier: '',
    warranty: '1 year',
    specifications: {}
  });
  const [imageFile, setImageFile] = useState<File | null>(null);
  const [imagePreview, setImagePreview] = useState<string | null>(null);

  // Mock products data
  const [products, setProducts] = useState<Product[]>([
    {
      id: '1',
      name: 'Premium Cotton T-Shirt',
      description: 'High-quality cotton t-shirt with excellent comfort and durability',
      price: 2500,
      category: 'Clothing',
      subcategory: 'T-Shirts',
      brand: 'FashionBrand',
      sku: 'TSH-001',
      barcode: '1234567890123',
      images: ['/product1.jpg'],
      status: 'active',
      inStock: true,
      stockQuantity: 150,
      minStockLevel: 20,
      maxStockLevel: 200,
      weight: 0.2,
      dimensions: { length: 70, width: 50, height: 5 },
      materials: ['Cotton'],
      colors: ['White', 'Black', 'Blue'],
      sizes: ['S', 'M', 'L', 'XL'],
      tags: ['casual', 'comfortable', 'cotton'],
      rating: 4.5,
      reviewCount: 25,
      createdAt: '2024-01-15T10:00:00Z',
      updatedAt: '2024-01-20T14:30:00Z',
      createdBy: 'admin@company.com',
      updatedBy: 'admin@company.com',
      cost: 1500,
      profitMargin: 40,
      supplier: 'TextileCorp',
      warranty: '1 year',
      specifications: {
        material: '100% Cotton',
        care: 'Machine washable',
        fit: 'Regular fit'
      },
      variants: []
    },
    {
      id: '2',
      name: 'Leather Wallet',
      description: 'Genuine leather wallet with multiple card slots',
      price: 3500,
      category: 'Accessories',
      subcategory: 'Wallets',
      brand: 'LeatherCraft',
      sku: 'WAL-001',
      barcode: '1234567890124',
      images: ['/product2.jpg'],
      status: 'active',
      inStock: true,
      stockQuantity: 75,
      minStockLevel: 10,
      maxStockLevel: 100,
      weight: 0.1,
      dimensions: { length: 10, width: 8, height: 2 },
      materials: ['Leather'],
      colors: ['Brown', 'Black'],
      sizes: ['Standard'],
      tags: ['leather', 'wallet', 'accessory'],
      rating: 4.8,
      reviewCount: 15,
      createdAt: '2024-01-10T09:00:00Z',
      updatedAt: '2024-01-18T16:45:00Z',
      createdBy: 'admin@company.com',
      updatedBy: 'admin@company.com',
      cost: 2000,
      profitMargin: 42.9,
      supplier: 'LeatherSupplies',
      warranty: '2 years',
      specifications: {
        material: 'Genuine Leather',
        slots: '6 card slots',
        coinPocket: 'Yes'
      },
      variants: []
    }
  ]);

  // Mock analytics
  const [analytics, setAnalytics] = useState<ProductAnalytics>({
    totalProducts: 0,
    activeProducts: 0,
    outOfStockProducts: 0,
    lowStockProducts: 0,
    totalValue: 0,
    averagePrice: 0,
    topCategories: [],
    topProducts: []
  });

  useEffect(() => {
    // Calculate analytics
    const totalProducts = products.length;
    const activeProducts = products.filter(p => p.status === 'active').length;
    const outOfStockProducts = products.filter(p => !p.inStock).length;
    const lowStockProducts = products.filter(p => p.stockQuantity <= p.minStockLevel).length;
    const totalValue = products.reduce((sum, p) => sum + (p.price * p.stockQuantity), 0);
    const averagePrice = totalProducts > 0 ? products.reduce((sum, p) => sum + p.price, 0) / totalProducts : 0;

    // Calculate top categories
    const categoryCounts: Record<string, number> = {};
    products.forEach(p => {
      categoryCounts[p.category] = (categoryCounts[p.category] || 0) + 1;
    });

    const topCategories = Object.entries(categoryCounts)
      .map(([name, count]) => ({
        name,
        count,
        percentage: (count / totalProducts) * 100
      }))
      .sort((a, b) => b.count - a.count)
      .slice(0, 5);

    setAnalytics({
      totalProducts,
      activeProducts,
      outOfStockProducts,
      lowStockProducts,
      totalValue,
      averagePrice,
      topCategories,
      topProducts: products.slice(0, 5).map(p => ({
        name: p.name,
        sales: Math.floor(Math.random() * 100) + 10,
        revenue: p.price * (Math.floor(Math.random() * 100) + 10)
      }))
    });
  }, [products]);

  const handleImageChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0] || null;
    setImageFile(file);
    if (file) {
      setImagePreview(URL.createObjectURL(file));
    } else {
      setImagePreview(null);
    }
  };

  const handleCreateProduct = async () => {
    if (!newProduct.name || !newProduct.description || !newProduct.price) {
      setError('Please fill in all required fields');
      return;
    }

    try {
      setLoading(true);
      setError(null);
      let imageFilename = '';
      if (imageFile) {
        const uploadResult = await uploadFile({ file: imageFile, type: 'product' });
        imageFilename = uploadResult.filename;
      }
      const product: Product = {
        id: Date.now().toString(),
        name: newProduct.name || '',
        description: newProduct.description || '',
        price: newProduct.price || 0,
        category: newProduct.category || '',
        subcategory: newProduct.subcategory || '',
        brand: newProduct.brand || '',
        sku: newProduct.sku || '',
        barcode: `BAR${Date.now()}`,
        images: imageFilename ? [`/uploads/products/${imageFilename}`] : [],
        status: newProduct.status || 'active',
        inStock: newProduct.inStock || true,
        stockQuantity: newProduct.stockQuantity || 0,
        minStockLevel: newProduct.minStockLevel || 10,
        maxStockLevel: newProduct.maxStockLevel || 100,
        weight: newProduct.weight || 0,
        dimensions: newProduct.dimensions || { length: 0, width: 0, height: 0 },
        materials: newProduct.materials || [],
        colors: newProduct.colors || [],
        sizes: newProduct.sizes || [],
        tags: newProduct.tags || [],
        rating: 0,
        reviewCount: 0,
        createdAt: new Date().toISOString(),
        updatedAt: new Date().toISOString(),
        createdBy: 'admin@company.com',
        updatedBy: 'admin@company.com',
        cost: newProduct.cost || 0,
        profitMargin: newProduct.price && newProduct.cost ? 
          ((newProduct.price - newProduct.cost) / newProduct.price) * 100 : 0,
        supplier: newProduct.supplier || '',
        warranty: newProduct.warranty || '1 year',
        specifications: newProduct.specifications || {},
        variants: []
      };

      setProducts(prev => [product, ...prev]);
      setNewProduct({
        name: '',
        description: '',
        price: 0,
        category: '',
        subcategory: '',
        brand: '',
        sku: '',
        status: 'active',
        inStock: true,
        stockQuantity: 0,
        minStockLevel: 10,
        maxStockLevel: 100,
        weight: 0,
        dimensions: { length: 0, width: 0, height: 0 },
        materials: [],
        colors: [],
        sizes: [],
        tags: [],
        cost: 0,
        supplier: '',
        warranty: '1 year',
        specifications: {}
      });
      setImageFile(null);
      setImagePreview(null);
      setOpenDialog(false);
    } catch (err) {
      setError('Failed to create product');
    } finally {
      setLoading(false);
    }
  };

  const handleUpdateProduct = async () => {
    if (!selectedProduct) return;

    try {
      setLoading(true);
      setError(null);

      const updatedProduct = {
        ...selectedProduct,
        updatedAt: new Date().toISOString(),
        updatedBy: 'admin@company.com'
      };

      setProducts(prev => prev.map(p => p.id === selectedProduct.id ? updatedProduct : p));
      setSelectedProduct(null);
      setOpenDialog(false);
    } catch (err) {
      setError('Failed to update product');
    } finally {
      setLoading(false);
    }
  };

  const handleDeleteProduct = async (id: string) => {
    if (!canManage('admin:products')) {
      setError('You do not have permission to delete products');
      return;
    }

    if (!window.confirm('Are you sure you want to delete this product?')) {
      return;
    }

    try {
      setProducts(prev => prev.filter(p => p.id !== id));
    } catch (err) {
      setError('Failed to delete product');
    }
  };

  const renderStatusChip = (status: Product['status']) => {
    const statusConfig = {
      active: { 
        background: 'linear-gradient(135deg, #43e97b 0%, #38f9d7 100%)',
        label: 'Active' 
      },
      inactive: { 
        background: 'linear-gradient(135deg, #a8edea 0%, #fed6e3 100%)',
        label: 'Inactive' 
      },
      discontinued: { 
        background: 'linear-gradient(135deg, #ff6b6b 0%, #ee5a24 100%)',
        label: 'Discontinued' 
      },
      out_of_stock: { 
        background: 'linear-gradient(135deg, #fa709a 0%, #fee140 100%)',
        label: 'Out of Stock' 
      }
    };

    const config = statusConfig[status];
    
    return (
      <Chip
        label={config.label}
        size="small"
        sx={{
          background: config.background,
          color: 'white',
          fontWeight: 600,
          fontSize: '0.75rem',
          boxShadow: '0px 2px 8px rgba(0, 0, 0, 0.1)',
        }}
      />
    );
  };

  const renderStockChip = (product: Product) => {
    if (!product.inStock) {
      return (
        <Chip 
          label="Out of Stock" 
          size="small"
          sx={{
            background: 'linear-gradient(135deg, #ff6b6b 0%, #ee5a24 100%)',
            color: 'white',
            fontWeight: 600,
            fontSize: '0.75rem',
            boxShadow: '0px 2px 8px rgba(0, 0, 0, 0.1)',
          }}
        />
      );
    }
    if (product.stockQuantity <= product.minStockLevel) {
      return (
        <Chip 
          label="Low Stock" 
          size="small"
          sx={{
            background: 'linear-gradient(135deg, #fa709a 0%, #fee140 100%)',
            color: 'white',
            fontWeight: 600,
            fontSize: '0.75rem',
            boxShadow: '0px 2px 8px rgba(0, 0, 0, 0.1)',
          }}
        />
      );
    }
    return (
      <Chip 
        label="In Stock" 
        size="small"
        sx={{
          background: 'linear-gradient(135deg, #43e97b 0%, #38f9d7 100%)',
          color: 'white',
          fontWeight: 600,
          fontSize: '0.75rem',
          boxShadow: '0px 2px 8px rgba(0, 0, 0, 0.1)',
        }}
      />
    );
  };

  const filteredProducts = products.filter(product => {
    const matchesSearch = product.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
                         product.description.toLowerCase().includes(searchTerm.toLowerCase()) ||
                         product.sku.toLowerCase().includes(searchTerm.toLowerCase()) ||
                         product.brand.toLowerCase().includes(searchTerm.toLowerCase());
    
    const matchesStatus = filter.status === 'all' || product.status === filter.status;
    const matchesCategory = filter.category === 'all' || product.category === filter.category;
    const matchesStock = filter.inStock === 'all' || 
                         (filter.inStock === 'in_stock' && product.inStock) ||
                         (filter.inStock === 'out_of_stock' && !product.inStock);
    
    return matchesSearch && matchesStatus && matchesCategory && matchesStock;
  });

  const sortedProducts = [...filteredProducts].sort((a, b) => {
    let aValue = a[sortBy as keyof Product];
    let bValue = b[sortBy as keyof Product];
    
    if (typeof aValue === 'string') aValue = aValue.toLowerCase();
    if (typeof bValue === 'string') bValue = bValue.toLowerCase();
    
    if (aValue < bValue) return sortOrder === 'asc' ? -1 : 1;
    if (aValue > bValue) return sortOrder === 'asc' ? 1 : -1;
    return 0;
  });

  return (
    <Box sx={{ p: 3 }}>
      <Box sx={{ mb: 4 }}>
        <Typography 
          variant="h3" 
          gutterBottom 
          sx={{
            fontWeight: 800,
            background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
            WebkitBackgroundClip: 'text',
            WebkitTextFillColor: 'transparent',
            mb: 2,
            display: 'flex',
            alignItems: 'center',
            gap: 2,
          }}
        >
          <Avatar
            sx={{
              background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
              width: 48,
              height: 48,
            }}
          >
            <CategoryIcon />
          </Avatar>
          Product Management
        </Typography>
        <Typography variant="body1" sx={{ color: 'text.secondary' }}>
          Manage your product catalog, inventory, and analytics
        </Typography>
      </Box>

      {error && (
        <Alert severity="error" sx={{ mb: 3 }}>
          {error}
        </Alert>
      )}

      {/* Analytics Cards */}
      <Grid container spacing={3} sx={{ mb: 4 }}>
        <Grid item xs={12} sm={6} md={3}>
          <Card
            sx={{
              background: 'linear-gradient(135deg, rgba(255, 255, 255, 0.95) 0%, rgba(255, 255, 255, 0.98) 100%)',
              backdropFilter: 'blur(10px)',
              border: '1px solid rgba(255, 255, 255, 0.2)',
              borderRadius: 3,
              boxShadow: '0px 8px 32px rgba(0, 0, 0, 0.1)',
              transition: 'all 0.3s ease',
              '&:hover': {
                transform: 'translateY(-4px)',
                boxShadow: '0px 12px 40px rgba(0, 0, 0, 0.15)',
              },
            }}
          >
            <CardContent sx={{ p: 3 }}>
              <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                <Avatar
                  sx={{
                    background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
                    width: 40,
                    height: 40,
                    mr: 2,
                  }}
                >
                  <CategoryIcon />
                </Avatar>
                <Typography variant="body2" sx={{ color: 'text.secondary', fontWeight: 500 }}>
                  Total Products
                </Typography>
              </Box>
              <Typography 
                variant="h4" 
                sx={{
                  fontWeight: 700,
                  background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
                  WebkitBackgroundClip: 'text',
                  WebkitTextFillColor: 'transparent',
                  mb: 1,
                }}
              >
                {analytics.totalProducts}
              </Typography>
              <LinearProgress 
                variant="determinate" 
                value={100} 
                sx={{ 
                  mt: 1,
                  height: 6,
                  borderRadius: 3,
                  backgroundColor: 'rgba(99, 102, 241, 0.1)',
                  '& .MuiLinearProgress-bar': {
                    background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
                    borderRadius: 3,
                  },
                }} 
              />
            </CardContent>
          </Card>
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <Card
            sx={{
              background: 'linear-gradient(135deg, rgba(255, 255, 255, 0.95) 0%, rgba(255, 255, 255, 0.98) 100%)',
              backdropFilter: 'blur(10px)',
              border: '1px solid rgba(255, 255, 255, 0.2)',
              borderRadius: 3,
              boxShadow: '0px 8px 32px rgba(0, 0, 0, 0.1)',
              transition: 'all 0.3s ease',
              '&:hover': {
                transform: 'translateY(-4px)',
                boxShadow: '0px 12px 40px rgba(0, 0, 0, 0.15)',
              },
            }}
          >
            <CardContent sx={{ p: 3 }}>
              <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                <Avatar
                  sx={{
                    background: 'linear-gradient(135deg, #43e97b 0%, #38f9d7 100%)',
                    width: 40,
                    height: 40,
                    mr: 2,
                  }}
                >
                  <CheckCircle />
                </Avatar>
                <Typography variant="body2" sx={{ color: 'text.secondary', fontWeight: 500 }}>
                  Active Products
                </Typography>
              </Box>
              <Typography 
                variant="h4" 
                sx={{
                  fontWeight: 700,
                  background: 'linear-gradient(135deg, #43e97b 0%, #38f9d7 100%)',
                  WebkitBackgroundClip: 'text',
                  WebkitTextFillColor: 'transparent',
                  mb: 1,
                }}
              >
                {analytics.activeProducts}
              </Typography>
              <LinearProgress 
                variant="determinate" 
                value={analytics.totalProducts ? (analytics.activeProducts / analytics.totalProducts) * 100 : 0} 
                sx={{ 
                  mt: 1,
                  height: 6,
                  borderRadius: 3,
                  backgroundColor: 'rgba(67, 233, 123, 0.1)',
                  '& .MuiLinearProgress-bar': {
                    background: 'linear-gradient(135deg, #43e97b 0%, #38f9d7 100%)',
                    borderRadius: 3,
                  },
                }} 
              />
            </CardContent>
          </Card>
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <Card
            sx={{
              background: 'linear-gradient(135deg, rgba(255, 255, 255, 0.95) 0%, rgba(255, 255, 255, 0.98) 100%)',
              backdropFilter: 'blur(10px)',
              border: '1px solid rgba(255, 255, 255, 0.2)',
              borderRadius: 3,
              boxShadow: '0px 8px 32px rgba(0, 0, 0, 0.1)',
              transition: 'all 0.3s ease',
              '&:hover': {
                transform: 'translateY(-4px)',
                boxShadow: '0px 12px 40px rgba(0, 0, 0, 0.15)',
              },
            }}
          >
            <CardContent sx={{ p: 3 }}>
              <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                <Avatar
                  sx={{
                    background: 'linear-gradient(135deg, #fa709a 0%, #fee140 100%)',
                    width: 40,
                    height: 40,
                    mr: 2,
                  }}
                >
                  <Warning />
                </Avatar>
                <Typography variant="body2" sx={{ color: 'text.secondary', fontWeight: 500 }}>
                  Out of Stock
                </Typography>
              </Box>
              <Typography 
                variant="h4" 
                sx={{
                  fontWeight: 700,
                  background: 'linear-gradient(135deg, #fa709a 0%, #fee140 100%)',
                  WebkitBackgroundClip: 'text',
                  WebkitTextFillColor: 'transparent',
                  mb: 1,
                }}
              >
                {analytics.outOfStockProducts}
              </Typography>
              <LinearProgress 
                variant="determinate" 
                value={analytics.outOfStockProducts > 0 ? 100 : 0} 
                sx={{ 
                  mt: 1,
                  height: 6,
                  borderRadius: 3,
                  backgroundColor: 'rgba(250, 112, 154, 0.1)',
                  '& .MuiLinearProgress-bar': {
                    background: 'linear-gradient(135deg, #fa709a 0%, #fee140 100%)',
                    borderRadius: 3,
                  },
                }} 
              />
            </CardContent>
          </Card>
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <Card
            sx={{
              background: 'linear-gradient(135deg, rgba(255, 255, 255, 0.95) 0%, rgba(255, 255, 255, 0.98) 100%)',
              backdropFilter: 'blur(10px)',
              border: '1px solid rgba(255, 255, 255, 0.2)',
              borderRadius: 3,
              boxShadow: '0px 8px 32px rgba(0, 0, 0, 0.1)',
              transition: 'all 0.3s ease',
              '&:hover': {
                transform: 'translateY(-4px)',
                boxShadow: '0px 12px 40px rgba(0, 0, 0, 0.15)',
              },
            }}
          >
            <CardContent sx={{ p: 3 }}>
              <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                <Avatar
                  sx={{
                    background: 'linear-gradient(135deg, #f093fb 0%, #f5576c 100%)',
                    width: 40,
                    height: 40,
                    mr: 2,
                  }}
                >
                  <AttachMoney />
                </Avatar>
                <Typography variant="body2" sx={{ color: 'text.secondary', fontWeight: 500 }}>
                  Total Value
                </Typography>
              </Box>
              <Typography 
                variant="h4" 
                sx={{
                  fontWeight: 700,
                  background: 'linear-gradient(135deg, #f093fb 0%, #f5576c 100%)',
                  WebkitBackgroundClip: 'text',
                  WebkitTextFillColor: 'transparent',
                  mb: 1,
                }}
              >
                ${analytics.totalValue.toLocaleString()}
              </Typography>
              <LinearProgress 
                variant="determinate" 
                value={75} 
                sx={{ 
                  mt: 1,
                  height: 6,
                  borderRadius: 3,
                  backgroundColor: 'rgba(240, 147, 251, 0.1)',
                  '& .MuiLinearProgress-bar': {
                    background: 'linear-gradient(135deg, #f093fb 0%, #f5576c 100%)',
                    borderRadius: 3,
                  },
                }} 
              />
            </CardContent>
          </Card>
        </Grid>
      </Grid>

      <Card
        sx={{
          background: 'linear-gradient(135deg, rgba(255, 255, 255, 0.95) 0%, rgba(255, 255, 255, 0.98) 100%)',
          backdropFilter: 'blur(10px)',
          border: '1px solid rgba(255, 255, 255, 0.2)',
          borderRadius: 3,
          boxShadow: '0px 8px 32px rgba(0, 0, 0, 0.1)',
          mb: 3,
        }}
      >
        <Tabs
          value={selectedTab}
          onChange={(_, newValue) => setSelectedTab(newValue)}
          indicatorColor="primary"
          textColor="primary"
          variant="fullWidth"
          sx={{
            '& .MuiTab-root': {
              fontWeight: 600,
              textTransform: 'none',
              fontSize: '1rem',
            },
          }}
        >
          <Tab label="Products" />
          <Tab label="Categories" />
          <Tab label="Analytics" />
        </Tabs>
      </Card>

      {selectedTab === 0 && (
        <Box>
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
            <Button
              variant="contained"
              startIcon={<Add />}
              onClick={() => setOpenDialog(true)}
              disabled={!canManage('manager:products') && !canManage('admin:products')}
              sx={{
                background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
                borderRadius: 2,
                textTransform: 'none',
                fontWeight: 600,
                px: 3,
                py: 1.5,
                boxShadow: '0px 4px 12px rgba(99, 102, 241, 0.3)',
                '&:hover': {
                  background: 'linear-gradient(135deg, #5a67d8 0%, #6b46c1 100%)',
                  boxShadow: '0px 6px 20px rgba(99, 102, 241, 0.4)',
                },
              }}
            >
              Add Product
            </Button>
            <Box sx={{ display: 'flex', gap: 2 }}>
              <TextField
                placeholder="Search products..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                size="small"
                sx={{
                  '& .MuiOutlinedInput-root': {
                    borderRadius: 2,
                    background: 'rgba(255, 255, 255, 0.8)',
                    backdropFilter: 'blur(10px)',
                    '&:hover': {
                      background: 'rgba(255, 255, 255, 0.9)',
                    },
                    '&.Mui-focused': {
                      background: 'rgba(255, 255, 255, 0.95)',
                    },
                  },
                }}
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <Search />
                    </InputAdornment>
                  ),
                }}
              />
              <FormControl size="small" sx={{ minWidth: 120 }}>
                <InputLabel>Status</InputLabel>
                <Select
                  value={filter.status}
                  onChange={(e) => setFilter(prev => ({ ...prev, status: e.target.value }))}
                  label="Status"
                  sx={{
                    borderRadius: 2,
                    background: 'rgba(255, 255, 255, 0.8)',
                    backdropFilter: 'blur(10px)',
                  }}
                >
                  <MenuItem value="all">All Status</MenuItem>
                  <MenuItem value="active">Active</MenuItem>
                  <MenuItem value="inactive">Inactive</MenuItem>
                  <MenuItem value="discontinued">Discontinued</MenuItem>
                  <MenuItem value="out_of_stock">Out of Stock</MenuItem>
                </Select>
              </FormControl>
              <FormControl size="small" sx={{ minWidth: 120 }}>
                <InputLabel>Category</InputLabel>
                <Select
                  value={filter.category}
                  onChange={(e) => setFilter(prev => ({ ...prev, category: e.target.value }))}
                  label="Category"
                  sx={{
                    borderRadius: 2,
                    background: 'rgba(255, 255, 255, 0.8)',
                    backdropFilter: 'blur(10px)',
                  }}
                >
                  <MenuItem value="all">All Categories</MenuItem>
                  <MenuItem value="Clothing">Clothing</MenuItem>
                  <MenuItem value="Accessories">Accessories</MenuItem>
                  <MenuItem value="Electronics">Electronics</MenuItem>
                </Select>
              </FormControl>
              <IconButton 
                onClick={() => setProducts([...products])}
                sx={{
                  background: 'linear-gradient(135deg, #4facfe 0%, #00f2fe 100%)',
                  color: 'white',
                  '&:hover': {
                    background: 'linear-gradient(135deg, #43e97b 0%, #38f9d7 100%)',
                  },
                }}
              >
                <Refresh />
              </IconButton>
            </Box>
          </Box>

          <Grid container spacing={3}>
            {sortedProducts.map((product) => (
              <Grid item xs={12} sm={6} md={4} lg={3} key={product.id}>
                <Card 
                  sx={{ 
                    height: '100%', 
                    display: 'flex', 
                    flexDirection: 'column',
                    background: 'linear-gradient(135deg, rgba(255, 255, 255, 0.95) 0%, rgba(255, 255, 255, 0.98) 100%)',
                    backdropFilter: 'blur(10px)',
                    border: '1px solid rgba(255, 255, 255, 0.2)',
                    borderRadius: 3,
                    boxShadow: '0px 8px 32px rgba(0, 0, 0, 0.1)',
                    transition: 'all 0.3s ease',
                    '&:hover': {
                      transform: 'translateY(-4px)',
                      boxShadow: '0px 12px 40px rgba(0, 0, 0, 0.15)',
                    },
                  }}
                >
                  <CardMedia
                    component="img"
                    height="200"
                    image={product.images[0] || '/uploads/products/placeholder.png' || 'https://via.placeholder.com/300x200'}
                    alt={product.name}
                    sx={{
                      borderTopLeftRadius: 12,
                      borderTopRightRadius: 12,
                    }}
                  />
                  <CardContent sx={{ flexGrow: 1, p: 3 }}>
                    <Typography variant="h6" gutterBottom sx={{ fontWeight: 600 }}>
                      {product.name}
                    </Typography>
                    <Typography variant="body2" sx={{ color: 'text.secondary', mb: 2 }}>
                      {product.description}
                    </Typography>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2 }}>
                      <Rating value={product.rating} readOnly size="small" />
                      <Typography variant="caption" sx={{ color: 'text.secondary' }}>
                        ({product.reviewCount})
                      </Typography>
                    </Box>
                    <Typography 
                      variant="h6" 
                      sx={{ 
                        fontWeight: 700,
                        background: 'linear-gradient(135deg, #f093fb 0%, #f5576c 100%)',
                        WebkitBackgroundClip: 'text',
                        WebkitTextFillColor: 'transparent',
                        mb: 2,
                      }}
                    >
                      ${product.price.toLocaleString()}
                    </Typography>
                    <Box sx={{ display: 'flex', gap: 1, mb: 2 }}>
                      {renderStatusChip(product.status)}
                      {renderStockChip(product)}
                    </Box>
                    <Typography variant="caption" sx={{ color: 'text.secondary' }}>
                      SKU: {product.sku} | Stock: {product.stockQuantity}
                    </Typography>
                  </CardContent>
                  <CardActions sx={{ p: 3, pt: 0 }}>
                    <Box sx={{ display: 'flex', gap: 1, width: '100%' }}>
                      <IconButton
                        size="small"
                        onClick={() => setViewingProduct(product)}
                        sx={{
                          background: 'linear-gradient(135deg, #4facfe 0%, #00f2fe 100%)',
                          color: 'white',
                          '&:hover': {
                            background: 'linear-gradient(135deg, #43e97b 0%, #38f9d7 100%)',
                          },
                        }}
                      >
                        <Visibility />
                      </IconButton>
                      <IconButton
                        size="small"
                        onClick={() => {
                          setSelectedProduct(product);
                          setOpenDialog(true);
                        }}
                        disabled={!canManage('manager:products') && !canManage('admin:products')}
                        sx={{
                          background: 'linear-gradient(135deg, #fa709a 0%, #fee140 100%)',
                          color: 'white',
                          '&:hover': {
                            background: 'linear-gradient(135deg, #f093fb 0%, #f5576c 100%)',
                          },
                        }}
                      >
                        <Edit />
                      </IconButton>
                      <IconButton
                        size="small"
                        onClick={() => handleDeleteProduct(product.id)}
                        disabled={!canManage('manager:products') && !canManage('admin:products')}
                        sx={{
                          background: 'linear-gradient(135deg, #ff6b6b 0%, #ee5a24 100%)',
                          color: 'white',
                          '&:hover': {
                            background: 'linear-gradient(135deg, #ee5a24 0%, #ff6b6b 100%)',
                          },
                        }}
                      >
                        <Delete />
                      </IconButton>
                    </Box>
                  </CardActions>
                </Card>
              </Grid>
            ))}
          </Grid>
        </Box>
      )}

      {selectedTab === 1 && (
        <Box>
          <Typography variant="h6" gutterBottom>
            Product Categories
          </Typography>
          <Grid container spacing={2}>
            {analytics.topCategories.map((category) => (
              <Grid item xs={12} md={6} lg={4} key={category.name}>
                <Card>
                  <CardHeader
                    title={category.name}
                    subheader={`${category.count} products`}
                  />
                  <CardContent>
                    <Typography variant="h4" color="primary.main">
                      {category.count}
                    </Typography>
                    <Typography variant="body2" color="textSecondary">
                      {category.percentage.toFixed(1)}% of total products
                    </Typography>
                    <LinearProgress 
                      variant="determinate" 
                      value={category.percentage} 
                      sx={{ mt: 1 }} 
                    />
                  </CardContent>
                </Card>
              </Grid>
            ))}
          </Grid>
        </Box>
      )}

      {selectedTab === 2 && (
        <Box>
          <Typography variant="h6" gutterBottom>
            Product Analytics
          </Typography>
          <Grid container spacing={2}>
            <Grid item xs={12} md={6}>
              <Card>
                <CardHeader title="Top Selling Products" />
                <CardContent>
                  <List>
                    {analytics.topProducts.map((product, index) => (
                      <ListItem key={index}>
                        <ListItemText
                          primary={product.name}
                          secondary={`${product.sales} units sold`}
                        />
                        <ListItemSecondaryAction>
                          <Typography variant="body2" color="primary.main">
                            ${product.revenue.toLocaleString()}
                          </Typography>
                        </ListItemSecondaryAction>
                      </ListItem>
                    ))}
                  </List>
                </CardContent>
              </Card>
            </Grid>
            <Grid item xs={12} md={6}>
              <Card>
                <CardHeader title="Stock Overview" />
                <CardContent>
                  <List>
                    <ListItem>
                      <ListItemText
                        primary="In Stock"
                        secondary={`${analytics.activeProducts - analytics.outOfStockProducts} products`}
                      />
                      <Chip label="Good" color="success" size="small" />
                    </ListItem>
                    <ListItem>
                      <ListItemText
                        primary="Low Stock"
                        secondary={`${analytics.lowStockProducts} products`}
                      />
                      <Chip label="Warning" color="warning" size="small" />
                    </ListItem>
                    <ListItem>
                      <ListItemText
                        primary="Out of Stock"
                        secondary={`${analytics.outOfStockProducts} products`}
                      />
                      <Chip label="Critical" color="error" size="small" />
                    </ListItem>
                  </List>
                </CardContent>
              </Card>
            </Grid>
          </Grid>
        </Box>
      )}

      {/* View Product Dialog */}
      <Dialog open={!!viewingProduct} onClose={() => setViewingProduct(null)} maxWidth="sm" fullWidth>
        <DialogTitle>Product Details</DialogTitle>
        <DialogContent>
          {viewingProduct && (
            <Box sx={{ mt: 1 }}>
              <Typography variant="body2"><strong>ID:</strong> {viewingProduct.id}</Typography>
              <Typography variant="body2"><strong>Name:</strong> {viewingProduct.name}</Typography>
              <Typography variant="body2"><strong>SKU:</strong> {viewingProduct.sku}</Typography>
              <Typography variant="body2"><strong>Price:</strong> ${viewingProduct.price}</Typography>
              <Typography variant="body2"><strong>Status:</strong> {viewingProduct.status}</Typography>
            </Box>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setViewingProduct(null)}>Close</Button>
          <Button
            variant="contained"
            onClick={() => {
              if (viewingProduct) {
                setSelectedProduct(viewingProduct);
                setOpenDialog(true);
                setViewingProduct(null);
              }
            }}
            disabled={!canManage('manager:products') && !canManage('admin:products')}
          >
            Edit
          </Button>
        </DialogActions>
      </Dialog>

      {/* Create/Edit Product Dialog */}
      <Dialog open={openDialog} onClose={() => setOpenDialog(false)} maxWidth="md" fullWidth>
        <DialogTitle>
          {selectedProduct ? 'Edit Product' : 'Add New Product'}
        </DialogTitle>
        <DialogContent>
          <Grid container spacing={2} sx={{ mt: 1 }}>
            <Grid item xs={12} md={6}>
              <TextField
                fullWidth
                label="Product Name *"
                value={selectedProduct?.name || newProduct.name}
                onChange={(e) => {
                  if (selectedProduct) {
                    setSelectedProduct({ ...selectedProduct, name: e.target.value });
                  } else {
                    setNewProduct({ ...newProduct, name: e.target.value });
                  }
                }}
                margin="normal"
              />
            </Grid>
            <Grid item xs={12} md={6}>
              <TextField
                fullWidth
                label="SKU *"
                value={selectedProduct?.sku || newProduct.sku}
                onChange={(e) => {
                  if (selectedProduct) {
                    setSelectedProduct({ ...selectedProduct, sku: e.target.value });
                  } else {
                    setNewProduct({ ...newProduct, sku: e.target.value });
                  }
                }}
                margin="normal"
              />
            </Grid>
            <Grid item xs={12}>
              <TextField
                fullWidth
                label="Description *"
                multiline
                rows={3}
                value={selectedProduct?.description || newProduct.description}
                onChange={(e) => {
                  if (selectedProduct) {
                    setSelectedProduct({ ...selectedProduct, description: e.target.value });
                  } else {
                    setNewProduct({ ...newProduct, description: e.target.value });
                  }
                }}
                margin="normal"
              />
            </Grid>
            <Grid item xs={12} md={6}>
              <TextField
                fullWidth
                label="Price *"
                type="number"
                value={selectedProduct?.price || newProduct.price}
                onChange={(e) => {
                  if (selectedProduct) {
                    setSelectedProduct({ ...selectedProduct, price: parseFloat(e.target.value) || 0 });
                  } else {
                    setNewProduct({ ...newProduct, price: parseFloat(e.target.value) || 0 });
                  }
                }}
                margin="normal"
              />
            </Grid>
            <Grid item xs={12} md={6}>
              <TextField
                fullWidth
                label="Cost"
                type="number"
                value={selectedProduct?.cost || newProduct.cost}
                onChange={(e) => {
                  if (selectedProduct) {
                    setSelectedProduct({ ...selectedProduct, cost: parseFloat(e.target.value) || 0 });
                  } else {
                    setNewProduct({ ...newProduct, cost: parseFloat(e.target.value) || 0 });
                  }
                }}
                margin="normal"
              />
            </Grid>
            <Grid item xs={12} md={6}>
              <FormControl fullWidth margin="normal">
                <InputLabel>Category</InputLabel>
                <Select
                  value={selectedProduct?.category || newProduct.category}
                  onChange={(e) => {
                    if (selectedProduct) {
                      setSelectedProduct({ ...selectedProduct, category: e.target.value });
                    } else {
                      setNewProduct({ ...newProduct, category: e.target.value });
                    }
                  }}
                  label="Category"
                >
                  <MenuItem value="Clothing">Clothing</MenuItem>
                  <MenuItem value="Accessories">Accessories</MenuItem>
                  <MenuItem value="Electronics">Electronics</MenuItem>
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={12} md={6}>
              <FormControl fullWidth margin="normal">
                <InputLabel>Status</InputLabel>
                <Select
                  value={selectedProduct?.status || newProduct.status}
                  onChange={(e) => {
                    if (selectedProduct) {
                      setSelectedProduct({ ...selectedProduct, status: e.target.value as Product['status'] });
                    } else {
                      setNewProduct({ ...newProduct, status: e.target.value as Product['status'] });
                    }
                  }}
                  label="Status"
                >
                  <MenuItem value="active">Active</MenuItem>
                  <MenuItem value="inactive">Inactive</MenuItem>
                  <MenuItem value="discontinued">Discontinued</MenuItem>
                  <MenuItem value="out_of_stock">Out of Stock</MenuItem>
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={12} md={6}>
              <TextField
                fullWidth
                label="Stock Quantity"
                type="number"
                value={selectedProduct?.stockQuantity || newProduct.stockQuantity}
                onChange={(e) => {
                  if (selectedProduct) {
                    setSelectedProduct({ ...selectedProduct, stockQuantity: parseInt(e.target.value) || 0 });
                  } else {
                    setNewProduct({ ...newProduct, stockQuantity: parseInt(e.target.value) || 0 });
                  }
                }}
                margin="normal"
              />
            </Grid>
            <Grid item xs={12} md={6}>
              <TextField
                fullWidth
                label="Brand"
                value={selectedProduct?.brand || newProduct.brand}
                onChange={(e) => {
                  if (selectedProduct) {
                    setSelectedProduct({ ...selectedProduct, brand: e.target.value });
                  } else {
                    setNewProduct({ ...newProduct, brand: e.target.value });
                  }
                }}
                margin="normal"
              />
            </Grid>
            <Grid item xs={12} md={6}>
              <Button
                variant="outlined"
                component="label"
                fullWidth
                sx={{ mb: 2 }}
              >
                Upload Product Image
                <input
                  type="file"
                  accept="image/*"
                  hidden
                  onChange={handleImageChange}
                />
              </Button>
              {imagePreview && (
                <Box sx={{ mt: 1, mb: 2 }}>
                  <img src={imagePreview} alt="Preview" style={{ maxWidth: '100%', maxHeight: 200 }} />
                </Box>
              )}
            </Grid>
          </Grid>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setOpenDialog(false)}>Cancel</Button>
          <Button
            onClick={selectedProduct ? handleUpdateProduct : handleCreateProduct}
            variant="contained"
            disabled={loading || (!canManage('manager:products') && !canManage('admin:products'))}
          >
            {loading ? <CircularProgress size={20} /> : (selectedProduct ? 'Update' : 'Create')}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}; 